import { createEvents } from "ics";

import { pluralized } from "common/utils/strings";
import { fromISODateTime } from "common/utils/dates";
import { appointmentKindToExoticKind } from "common/constants/scheduler";

import { ClassifyAppointmentKindResult } from "client/turbo/types";
import { PatientForMultiSelectPanel } from "client/accounts/components/PatientMultiSelectPanel";

import { ClientAppointment, CalendarInfo, CalendarInfoAppointmentKindData } from "../types";

// Strongly inspired by the example here:
// https://github.com/adamgibbons/ics?tab=readme-ov-file#using-esmodules--in-the-browser
export const downloadIcsForAppointments = async (appointments: ClientAppointment[]) => {
  if (!appointments.length || !appointments.every((a) => a.start_datetime && a.end_datetime)) {
    return;
  }

  const events = appointments.map((appt) => ({
    start: fromISODateTime(appt.start_datetime).getTime(),
    end: fromISODateTime(appt.end_datetime).getTime(),
    title: `${appt.kind?.label ?? "Appointment"} for ${appt.patient.name_display}${
      appt.resource ? ` with ${appt.resource.name}` : ""
    }`,
  }));

  const filename = ".ics";
  const file = await new Promise<File>((resolve, reject) => {
    createEvents(events, (error, value) => {
      if (error) {
        console.warn("Error creating ICS file", error);
        reject(error);
      }

      resolve(
        new File([value], `${pluralized(appointments.length, "appointment")}.ics`, {
          type: "text/calendar",
        }),
      );
    });
  });

  const url = URL.createObjectURL(file);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

export type AppointmentKindInfo = {
  appointmentKind: CalendarInfoAppointmentKindData | null;
  appointmentKindBookable: boolean;
  blockedPatient: PatientForMultiSelectPanel | null;
  isEmergency: boolean;
};

/**
 * Gets the enabled appointment kind for a specific species, handling exotic species mapping
 */
export const getAppointmentBookingInfo = (
  classification: ClassifyAppointmentKindResult | null,
  patient: PatientForMultiSelectPanel,
  info: CalendarInfo,
): AppointmentKindInfo => {
  const classifiedAppointmentKind = classification?.appointment_kind;
  const isEmergency = classification?.is_emergency ?? false;

  let enabledAppointmentKind =
    classifiedAppointmentKind &&
    info.enabled_appointment_kinds.find((kind) => kind.slug === classifiedAppointmentKind);
  let appointmentKindBookable = !!enabledAppointmentKind?.is_client_bookable;
  let blockedPatient = null;

  const isExoticSpecies = !info.non_exotic_species.includes(patient.species);
  const exoticEquivalentKind =
    classifiedAppointmentKind && appointmentKindToExoticKind[classifiedAppointmentKind];

  // If species is exotic and appt kind has exotic equivalent, use exotic equivalent
  if (isExoticSpecies && exoticEquivalentKind) {
    const enabledExoticAppointmentKind = info.enabled_appointment_kinds.find(
      (kind) => kind.slug === exoticEquivalentKind,
    );
    const isExoticAppointmentKindBookable = !!enabledExoticAppointmentKind?.is_client_bookable;
    // If base appt kind is bookable but exotic equivalent is not, send blocked species to error drawer to display proper message
    if (enabledAppointmentKind?.is_client_bookable && !isExoticAppointmentKindBookable) {
      blockedPatient = patient;
    }
    // Use the exotic equivalent values
    enabledAppointmentKind = enabledExoticAppointmentKind;
    appointmentKindBookable = isExoticAppointmentKindBookable;

    // Check if species is available for booking at this clinic
    // This should get blocked at species selection step, but check here just in case
    if (info.allowed_species.length > 0 && !info.allowed_species.includes(patient.species)) {
      enabledAppointmentKind = null;
      appointmentKindBookable = false;
      blockedPatient = patient;
    }
  }

  return {
    appointmentKind: enabledAppointmentKind ?? null,
    appointmentKindBookable,
    blockedPatient,
    isEmergency,
  };
};
